import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle, Truck, Zap, HardHat, ShieldCheck, BadgeCheck, FileCheck2,
  CalendarDays, Filter, Download,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Card, Pill, Button, KPI, Spinner, EmptyState } from "@/components/ui";
import { fmtDate, daysFromNow } from "@/lib/dates";

// =============================================================================
// Compliance calendar — design doc 5.7
// =============================================================================
// Reads the v_compliance_calendar view which aggregates every expiring item
// in the business: licences, vehicle rego, tool T&T, rigging destroy-by,
// insurance, policy reviews, subbie insurance.
// =============================================================================

interface Row {
  category: string;       // licence | vehicle_rego | test_and_tag | rigging_destroy_by | insurance | policy_review | subbie_insurance_pl
  item_id: string;
  display_name: string;
  due_date: string | null;
  urgency: "expired" | "critical" | "warning" | "notice" | "ok" | "overdue";
  source_table: string;
  owner_id: string | null;
}

const CATEGORY_META: Record<string, { label: string; icon: typeof Truck; color: string }> = {
  licence:              { label: "Licences",           icon: BadgeCheck,    color: "text-info" },
  vehicle_rego:         { label: "Vehicle rego",       icon: Truck,         color: "text-info" },
  test_and_tag:         { label: "Test & Tag",         icon: Zap,           color: "text-warn" },
  rigging_destroy_by:   { label: "Rigging destroy-by", icon: HardHat,       color: "text-warn" },
  insurance:            { label: "Insurance",          icon: ShieldCheck,   color: "text-info" },
  subbie_insurance_pl:  { label: "Subbie PL insurance",icon: ShieldCheck,   color: "text-info" },
  policy_review:        { label: "Policy reviews",     icon: FileCheck2,    color: "text-info" },
};

const URGENCY_ORDER: Row["urgency"][] = ["expired", "overdue", "critical", "warning", "notice", "ok"];

export default function ComplianceScreen() {
  const [filter, setFilter] = useState<"all" | Row["urgency"]>("all");
  const [category, setCategory] = useState<string>("all");

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["compliance_calendar"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("v_compliance_calendar")
        .select("*")
        .order("due_date", { ascending: true });
      if (error) throw error;
      return (data as Row[]) ?? [];
    },
    refetchInterval: 5 * 60_000,
  });

  // -- Stats for the KPI strip ---------------------------------------------
  const stats = useMemo(() => {
    const by = (u: Row["urgency"]) => rows.filter(r => r.urgency === u).length;
    return {
      total:    rows.length,
      expired:  by("expired") + by("overdue"),
      critical: by("critical"),
      warning:  by("warning"),
      notice:   by("notice"),
    };
  }, [rows]);

  // -- Filtered rows --------------------------------------------------------
  const filtered = useMemo(() => {
    let r = rows;
    if (filter !== "all") r = r.filter(x => x.urgency === filter);
    if (category !== "all") r = r.filter(x => x.category === category);
    return r;
  }, [rows, filter, category]);

  // -- Grouped by category (for the section view) ---------------------------
  const byCategory = useMemo(() => {
    const out: Record<string, Row[]> = {};
    for (const r of filtered) (out[r.category] ||= []).push(r);
    return out;
  }, [filtered]);

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <div className="mb-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Compliance Calendar</h1>
          <div className="text-text-muted text-[13px] mt-1">
            Every expiring licence, registration, inspection and policy across the business. Auto-refreshes every 5 minutes.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" icon={Download} size="sm">Export CSV</Button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <KPI label="Tracked items" value={stats.total} icon={CalendarDays} />
        <KPI label="Expired" value={stats.expired} accent={stats.expired > 0} />
        <KPI label="Critical (≤7d)" value={stats.critical} sub="action this week" />
        <KPI label="Warning (≤30d)" value={stats.warning} />
        <KPI label="Notice (≤60d)" value={stats.notice} />
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-1 text-text-muted text-[11px] uppercase tracking-wider2">
          <Filter size={12} /> Urgency
        </div>
        {(["all", ...URGENCY_ORDER] as const).map(u => (
          <button
            key={u}
            onClick={() => setFilter(u)}
            className={`px-3 py-1.5 rounded text-[11px] font-mono uppercase tracking-wider2 border transition-colors ${
              filter === u
                ? "bg-accent/12 text-accent border-accent/30"
                : "bg-ink-raised text-text-muted border-line hover:text-text"
            }`}
          >
            {u}{u !== "all" && ` (${rows.filter(r => r.urgency === u).length})`}
          </button>
        ))}

        <div className="flex items-center gap-1 text-text-muted text-[11px] uppercase tracking-wider2 ml-2">
          Category
        </div>
        <select
          value={category}
          onChange={e => setCategory(e.target.value)}
          className="px-3 py-1.5 bg-ink-raised border border-line rounded text-[12px] text-text"
        >
          <option value="all">All categories</option>
          {Object.entries(CATEGORY_META).map(([k, m]) => (
            <option key={k} value={k}>{m.label}</option>
          ))}
        </select>
      </div>

      {/* Body */}
      {isLoading ? (
        <div className="py-20 flex justify-center"><Spinner size={20} /></div>
      ) : filtered.length === 0 ? (
        <Card>
          <EmptyState
            title="All clear"
            description="Nothing matches the current filter. Either everything is in date, or broaden the filter."
            icon={ShieldCheck}
          />
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(byCategory).map(([cat, list]) => {
            const meta = CATEGORY_META[cat] ?? { label: cat, icon: AlertTriangle, color: "text-text-muted" };
            const Icon = meta.icon;
            return (
              <section key={cat}>
                <div className="flex items-center gap-2 mb-3">
                  <Icon size={15} className={meta.color} />
                  <h2 className="text-[13px] font-semibold uppercase tracking-wider2">{meta.label}</h2>
                  <span className="text-text-muted text-[11px] font-mono">· {list.length}</span>
                </div>
                <Card>
                  {list.map((r, i) => {
                    const days = daysFromNow(r.due_date);
                    return (
                      <div
                        key={r.item_id + r.category}
                        className={`flex items-center gap-4 px-4 py-3 ${
                          i < list.length - 1 ? "border-b border-line" : ""
                        }`}
                      >
                        <div className={`w-1 h-10 rounded-full ${urgencyBarClass(r.urgency)}`} />
                        <div className="flex-1 min-w-0">
                          <div className="text-text text-[14px]">{r.display_name}</div>
                          <div className="text-text-muted text-[11px] font-mono mt-0.5">
                            Due {fmtDate(r.due_date)}
                            {days !== null && (
                              <span className={`ml-2 ${urgencyTextClass(r.urgency)}`}>
                                · {days < 0 ? `${Math.abs(days)}d overdue` : days === 0 ? "today" : `${days}d`}
                              </span>
                            )}
                          </div>
                        </div>
                        <Pill label={r.urgency} tone={r.urgency} small />
                      </div>
                    );
                  })}
                </Card>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}

function urgencyBarClass(u: Row["urgency"]): string {
  switch (u) {
    case "expired":
    case "overdue":
    case "critical":
      return "bg-danger";
    case "warning": return "bg-warn";
    case "notice":  return "bg-info";
    default:        return "bg-ok";
  }
}

function urgencyTextClass(u: Row["urgency"]): string {
  switch (u) {
    case "expired":
    case "overdue":
    case "critical":
      return "text-danger";
    case "warning": return "text-warn";
    case "notice":  return "text-info";
    default:        return "text-ok";
  }
}
